import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
	UKETSVoyageColumnsData,
	UKETSVoyagesEtsColumnsData,
} from "../../../../../../../../src/constants/CustomTableHeaders";
import CustomTable from "../../../../../../../../src/common/customTable/CustomTable";
import axiosHttp from "../../../../../../../../src/services/interceptor";
import Loader from "../../../../../../../../src/common/Loader/Loader";
import "./UKETSVoyages.scss";

const UKETSVoyages = () => {
	const [UKETSData, setUKETSData] = useState([]);
	const [loading, setLoading] = useState(false);

	const reportDetails = useSelector(
		(state) => state.reports.selectedReportDetails
	);


	const formatDateTime = (date) => {
		if (!date) return "";

		const parsedDate = new Date(date);

		if (Number.isNaN(parsedDate.getTime())) {
			return date;
		}

		const day = String(parsedDate.getUTCDate()).padStart(2, "0");

		const monthNames = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];

		const month = monthNames[parsedDate.getUTCMonth()];
		const year = parsedDate.getUTCFullYear();

		const hours = String(parsedDate.getUTCHours()).padStart(2, "0");
		const minutes = String(parsedDate.getUTCMinutes()).padStart(2, "0");
		const seconds = String(parsedDate.getUTCSeconds()).padStart(2, "0");

		return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
	};


	const sanitizeKey = (value) => {
		if (!value) return "";

		return value
			.trim()
			.replace(/[^a-zA-Z0-9]+/g, "_")
			.replace(/^_+|_+$/g, "");
	};


	const fuelConsumptionHeader = (fuelName) => (
		<div
			style={{
				textAlign: "center",
				width: "100%",
				whiteSpace: "normal",
				wordBreak: "break-word",
			}}
		>
			<div>{fuelName}</div>
			<div>Cons (MT)</div>
		</div>
	);

	const consumerFuelHeader = (consumer, fuelName) => (
		<div
			style={{
				textAlign: "center",
				width: "100%",
				whiteSpace: "normal",
				wordBreak: "break-word",
				lineHeight: "1.4",
			}}
		>
			<div>{consumer}</div>
			<div>{fuelName}</div>
			<div>Cons (MT)</div>
		</div>
	);

	const mncHeader = (fuelName) => (
		<div
			style={{
				textAlign: "center",
				width: "100%",
				whiteSpace: "normal",
				wordBreak: "break-word",
			}}
		>
			<div>Mi,NC</div>
			<div>{fuelName}</div>
		</div>
	);

	const consumerMncHeader = (consumer, fuelName) => (
		<div
			style={{
				textAlign: "center",
				width: "100%",
				whiteSpace: "normal",
				wordBreak: "break-word",
				lineHeight: "1.4",
			}}
		>
			<div>{consumer}</div>
			<div>Mi,NC</div>
			<div>{fuelName}</div>
		</div>
	);

	const twoLineHeader = (line1, line2) => (
		<div
			style={{
				textAlign: "center",
				width: "100%",
				lineHeight: "1.6",
			}}
		>
			<div>{line1}</div>
			<div>{line2}</div>
		</div>
	);


	// Get total fuel consumption

	const getFuelConsumption = (voyage, fuelName) => {
		const fuel = voyage?.voyageConsumptions?.find(
			(item) =>
				item?.fuelName?.toLowerCase() === fuelName?.toLowerCase()
		);

		return fuel?.consumption ?? 0;
	};


	// Get consumer-wise fuel consumption

	const getConsumerFuelConsumption = (
		voyage,
		fuelName,
		consumerCode
	) => {
		const fuel =
			voyage?.voyageConsumerWiseConsumptions?.find(
				(item) =>
					item?.fuelName?.toLowerCase() ===
					fuelName?.toLowerCase() &&
					item?.consumerCode?.toLowerCase() ===
					consumerCode?.toLowerCase()
			);

		return fuel?.consumption ?? 0;
	};


	// Get total Mi,NC

	const getMnc = (voyage, fuelName) => {
		const mnc = voyage?.voyageMiNcConsumptions?.find(
			(item) =>
				item?.fuelName?.toLowerCase() ===
				fuelName?.toLowerCase()
		);

		return mnc?.consumption ?? 0;
	};


	// Get consumer-wise Mi,NC

	const getConsumerMnc = (
		voyage,
		fuelName,
		consumerCode
	) => {
		const mnc =
			voyage?.voyageMiNcConsumerWiseConsumptions?.find(
				(item) =>
					item?.fuelName?.toLowerCase() ===
					fuelName?.toLowerCase() &&
					item?.consumerCode?.toLowerCase() ===
					consumerCode?.toLowerCase()
			);

		return mnc?.consumption ?? 0;
	};


	// Get all dynamic fuel names

	const fuelNames = useMemo(() => {
		const fuelSet = new Set();

		UKETSData?.forEach((voyage) => {
			voyage?.voyageConsumptions?.forEach((fuel) => {
				if (fuel?.fuelName) {
					fuelSet.add(fuel.fuelName);
				}
			});

			voyage?.voyageConsumerWiseConsumptions?.forEach((fuel) => {
				if (fuel?.fuelName) {
					fuelSet.add(fuel.fuelName);
				}
			});

			voyage?.voyageMiNcConsumptions?.forEach((fuel) => {
				if (fuel?.fuelName) {
					fuelSet.add(fuel.fuelName);
				}
			});

			voyage?.voyageMiNcConsumerWiseConsumptions?.forEach(
				(fuel) => {
					if (fuel?.fuelName) {
						fuelSet.add(fuel.fuelName);
					}
				}
			);
		});

		return Array.from(fuelSet);
	}, [UKETSData]);


	const consumerCodes = useMemo(() => {
		const consumers = new Set();

		UKETSData?.forEach((voyage) => {
			voyage?.voyageConsumerWiseConsumptions?.forEach(
				(item) => {
					if (item?.consumerCode?.trim()) {
						consumers.add(
							item.consumerCode.trim().toUpperCase()
						);
					}
				}
			);

			voyage?.voyageMiNcConsumerWiseConsumptions?.forEach(
				(item) => {
					if (item?.consumerCode?.trim()) {
						consumers.add(
							item.consumerCode.trim().toUpperCase()
						);
					}
				}
			);
		});

		const priorityConsumers = ["ME", "AE", "BOILER"];

		return Array.from(consumers).sort((a, b) => {
			const indexA = priorityConsumers.indexOf(a);
			const indexB = priorityConsumers.indexOf(b);

			if (indexA !== -1 && indexB !== -1) {
				return indexA - indexB;
			}

			if (indexA !== -1) {
				return -1;
			}

			if (indexB !== -1) {
				return 1;
			}

			return a.localeCompare(b);
		});
	}, [UKETSData]);

	const tableData = useMemo(() => {
		return (
			UKETSData?.map((voyage, index) => {
				const row = {
					voyageNumber: index + 1,

					isSampleVoyage: voyage?.isSampleVoyage
						?? false,
					portOfDeparture:
						voyage?.portOfDeparture || "",

					portOfArrival:
						voyage?.portOfArrival || "",

					departureTime: formatDateTime(
						voyage?.departureTime
					),

					arrivalTime: formatDateTime(
						voyage?.arrivalTime
					),

					betweenGBandNI: voyage?.isBetweenGbAndNi
						? "Y"
						: "N",

					voyageDuration:
						voyage?.voyageDuration ?? 0,

					timeAtSea:
						voyage?.timeAtSea ?? 0,

					timeAtAnchor:
						voyage?.timeAtAnchor ?? 0,

					calcTimeAtSea:
						voyage?.calcTimeAtSea ?? 0,

					distanceTravelled:
						voyage?.distanceTravelled ?? 0,

					calcDistanceTravelled:
						voyage?.calcDistanceTravelled ?? 0,

					sailingInIce: voyage?.sailingInIce
						? "Y"
						: "N",

					cargoMt:
						voyage?.cargoMt ?? 0,

					co2Ets:
						voyage?.co2Ets ?? 0,

					ch4Ets:
						voyage?.ch4Ets ?? 0,

					n2oEts:
						voyage?.n2oEts ?? 0,

					meEts:
						voyage?.meEts ?? 0,
				};

				// Dynamic fuel + consumer + Mi,NC columns

				fuelNames.forEach((fuelName) => {
					const fuelKey = sanitizeKey(fuelName);

					// Total fuel consumption
					row[`fuel_${fuelKey}`] =
						getFuelConsumption(
							voyage,
							fuelName
						);

					// Consumer-wise fuel consumption

					consumerCodes.forEach((consumerCode) => {
						const consumerKey =
							sanitizeKey(consumerCode);

						row[
							`consumer_${consumerKey}_fuel_${fuelKey}`
						] = getConsumerFuelConsumption(
							voyage,
							fuelName,
							consumerCode
						);

						// Consumer-wise Mi,NC

						row[
							`consumer_${consumerKey}_mnc_${fuelKey}`
						] = getConsumerMnc(
							voyage,
							fuelName,
							consumerCode
						);
					});

					// Total Mi,NC
					row[`mnc_${fuelKey}`] =
						getMnc(voyage, fuelName);
				});

				return row;
			}) || []
		);
	}, [
		UKETSData,
		fuelNames,
		consumerCodes,
	]);

	const neverHideColumns = new Set([
		"voyageNumber",
		"isSampleVoyage",
		"portOfDeparture",
		"portOfArrival",
		"departureTime",
		"arrivalTime",
		"betweenGBandNI",
		"sailingInIce",
	]);


	const zeroColumnIds = useMemo(() => {
		if (!tableData?.length) {
			return new Set();
		}

		const allKeys = new Set();

		tableData.forEach((row) => {
			Object.keys(row).forEach((key) => {
				allKeys.add(key);
			});
		});

		const allZero = new Set();

		allKeys.forEach((key) => {
			if (neverHideColumns.has(key)) {
				return;
			}

			const isAllZero = tableData.every((row) => {
				const val = parseFloat(row[key]);

				return !isNaN(val) && val === 0;
			});

			if (isAllZero) {
				allZero.add(key);
			}
		});

		return allZero;
	}, [tableData]);


	const compactHeadCell = (
		existingSx = {},
		valign = "middle"
	) => ({
		sx: {
			padding: "6px 6px",

			"& .Mui-TableHeadCell-Content": {
				height: "100%",
				alignItems:
					valign === "top"
						? "flex-start"
						: "center",
				display: "flex",
			},

			...existingSx,
		},
	});


	const columns = useMemo(() => {

		const fuelColumns = fuelNames.map((fuelName) => {
			const fuelKey = sanitizeKey(fuelName);

			return {
				id: `fuel_${fuelKey}`,

				header: fuelConsumptionHeader(
					fuelName
				),

				accessorKey: `fuel_${fuelKey}`,

				muiTableHeadCellProps:
					compactHeadCell({}, "middle"),
			};
		});

		// DYNAMIC CONSUMER FUEL COLUMNS

		const consumerFuelColumns =
			consumerCodes.flatMap(
				(consumerCode) => {
					const consumerKey =
						sanitizeKey(consumerCode);

					return fuelNames.map(
						(fuelName) => {
							const fuelKey =
								sanitizeKey(fuelName);

							return {
								id: `consumer_${consumerKey}_fuel_${fuelKey}`,

								header:
									consumerFuelHeader(
										consumerCode,
										fuelName
									),

								accessorKey: `consumer_${consumerKey}_fuel_${fuelKey}`,

								muiTableHeadCellProps:
									compactHeadCell(
										{},
										"top"
									),
							};
						}
					);
				}
			);

		// TOTAL Mi,NC COLUMNS

		const mncColumns = fuelNames
			.filter((fuelName) =>
				UKETSData?.some((voyage) =>
					voyage?.voyageMiNcConsumptions?.some(
						(item) =>
							item?.fuelName?.toLowerCase() ===
							fuelName?.toLowerCase()
					)
				)
			)
			.map((fuelName) => {
				const fuelKey =
					sanitizeKey(fuelName);

				return {
					id: `mnc_${fuelKey}`,

					header: mncHeader(fuelName),

					accessorKey: `mnc_${fuelKey}`,

					muiTableHeadCellProps:
						compactHeadCell(
							{},
							"middle"
						),
				};
			});


		// DYNAMIC CONSUMER Mi,NC COLUMNS

		const consumerMncColumns =
			consumerCodes.flatMap(
				(consumerCode) => {
					const consumerKey =
						sanitizeKey(consumerCode);

					return fuelNames
						.filter((fuelName) =>
							UKETSData?.some((voyage) =>
								voyage?.voyageMiNcConsumerWiseConsumptions?.some(
									(item) =>
										item?.fuelName
											?.toLowerCase() ===
										fuelName?.toLowerCase() &&
										item?.consumerCode
											?.toUpperCase() ===
										consumerCode.toUpperCase()
								)
							)
						)
						.map((fuelName) => {
							const fuelKey =
								sanitizeKey(fuelName);

							return {
								id: `consumer_${consumerKey}_mnc_${fuelKey}`,

								header:
									consumerMncHeader(
										consumerCode,
										fuelName
									),

								accessorKey: `consumer_${consumerKey}_mnc_${fuelKey}`,

								muiTableHeadCellProps:
									compactHeadCell(
										{},
										"top"
									),
							};
						});
				}
			);


		const sizeMap = {
			voyageNumber: 100,
			isSampleVoyage: 100,
			portOfDeparture: 170,
			portOfArrival: 170,
			departureTime: 250,
			arrivalTime: 250,
		};

		const patchedFixedColumns = (
			UKETSVoyageColumnsData || []
		)
			.filter(Boolean)
			.map((col) => {
				if (!col) {
					return col;
				}

				const patched = {
					...col,

					size:
						sizeMap[col.id] ??
						col.size ??
						170,

					muiTableHeadCellProps:
						compactHeadCell(
							col.muiTableHeadCellProps?.sx,
							"middle"
						),
				};

				if (col.id === "voyageNumber") {
					patched.header =
						twoLineHeader(
							"Voyage",
							"Number"
						);
				}

				if (col.id === "isSampleVoyage") {
					patched.header =
						twoLineHeader(
							"Is Sample",
							"Voyage"
						);
				}

				if (col.id === "betweenGBandNI") {
					patched.header =
						twoLineHeader(
							"Between GB",
							"and NI (Y/N)"
						);

					patched.Cell = ({ cell }) =>
						cell.getValue()
							? "Y"
							: "N";
				}

				if (col.id === "departureTime") {
					patched.header =
						twoLineHeader(
							"Date and Time of",
							"Departure (UTC)"
						);
				}

				if (col.id === "arrivalTime") {
					patched.header =
						twoLineHeader(
							"Date and Time of",
							"Arrival (UTC)"
						);
				}

				return patched;
			});


		// ETS columns


		const patchedEtsColumns = (
			UKETSVoyagesEtsColumnsData || []
		)
			.filter(Boolean)
			.map((col) => ({
				...col,

				size: 90,

				muiTableHeadCellProps:
					compactHeadCell(
						col.muiTableHeadCellProps?.sx,
						"middle"
					),
			}));

		const allColumns = [
			...patchedFixedColumns,

			...fuelColumns,

			...consumerFuelColumns,

			...mncColumns,

			...consumerMncColumns,

			...patchedEtsColumns,
		];

		return allColumns
			.filter(Boolean)
			.filter(
				(col) =>
					!zeroColumnIds.has(col.id)
			);
	}, [
		fuelNames,
		consumerCodes,
		UKETSData,
		zeroColumnIds,
	]);


	const fetchUKETSVoyagesData = async () => {
		setLoading(true);

		try {
			const response = await axiosHttp({
				url: `api/UkEts/GetUkEtsVoyages?childReportId=${reportDetails?.ReportMstId}`,
				method: "GET",
			});

			const responseData = response?.data;

			console.log(
				"API RESPONSE:",
				responseData
			);

			if (responseData?.data) {
				setUKETSData(
					responseData.data
						?.ukEtsVoyagesLists || []
				);
			} else {
				setUKETSData(
					responseData
						?.ukEtsVoyagesLists || []
				);
			}
		} catch (error) {
			console.error(
				"Error while fetching UK ETS voyages:",
				error
			);

			setUKETSData([]);
		} finally {
			setLoading(false);
		}
	};


	useEffect(() => {
		fetchUKETSVoyagesData();
	}, []);


	const columnOrder = useMemo(() => {
		return columns.map(
			(column) => column.id
		);
	}, [columns]);

	return (
		<>
			{loading ? (
				<div className="loader">
					<Loader />
				</div>
			) : (
				<div className="UKETS-custom-table-container">
					<CustomTable
						columnsData={columns}
						renderTableData={tableData}
						isUkEtsChildReport={true}
						customProps={{
							isLoading: loading,

							// Dynamic column order
							columnOrder: columnOrder,

							enableSort: false,

							enableToptoolbar: false,

							enableColumnVirtualization: false,

							enableRowVirtualization: true,

							enableColumnPinning: true,

							columnPinning: {
								left: [
									"voyageNumber",
									"isSampleVoyage",
									"portOfDeparture",
									"portOfArrival",
									"departureTime",
									"arrivalTime",
								],
							},

							muiTableHeadCellProps: {
								sx: {
									verticalAlign: "middle",

									"& .Mui-TableHeadCell-Content": {
										justifyContent: "center",
										alignItems: "center",
									},

									"& .Mui-TableHeadCell-Content-Wrapper": {
										textAlign: "center",
										width: "100%",
									},
								},
							},
						}}
					/>
				</div>
			)}
		</>
	);
};

export default UKETSVoyages;